import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { ClientSessionService } from '@brighthub/uisession';
import { CopilotContextComposerService } from '@brighthub/copilot';
import { ClientFinancesStateService } from './client-finances-state.service';
import { ColResizeDirective } from '../client-actions/col-resize.directive';
import {
  AssetCategory,
  Bill,
  BillRisk,
  ClientFinancesState,
  CreditScoreBand,
  DebtItem,
  DebtType,
  FinancialInsight,
  IncomeSource,
  InsightSeverity,
} from './client-finances.model';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });

@Component({
  selector: 'bai-client-finances',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ColResizeDirective, DecimalPipe, SkeletonModule, TagModule, TitleCasePipe],
  templateUrl: './client-finances.component.html',
  styleUrl: './client-finances.component.scss',
})
export class ClientFinancesComponent {
  private readonly _session = inject(ClientSessionService);
  private readonly _stateService = inject(ClientFinancesStateService);
  private readonly _copilotComposer = inject(CopilotContextComposerService);

  readonly loading = signal(true);
  private readonly _lastInitializedSessionId = signal<string | null>(null);

  readonly creditDetailsOpen = signal(true);
  readonly debtDetailsOpen = signal(false);
  readonly affordabilityDetailsOpen = signal(false);
  readonly assetsDetailsOpen = signal(false);
  readonly billsDetailsOpen = signal(false);

  private readonly _sessionId = computed(
    () => this._session.selectedSession()?.uiSessionId ?? null
  );

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) {
        this._stateService.ensureState(id);
        this.loading.set(false);
      } else {
        this.loading.set(false);
      }
    });

    // Initialize expansion defaults once per selected session.
    effect(() => {
      const id = this._sessionId();
      const state = this.state();
      if (!id || !state || this._lastInitializedSessionId() === id) return;

      this.creditDetailsOpen.set(true);
      this.debtDetailsOpen.set(this.highRiskDebtCount(state) > 0);
      this.affordabilityDetailsOpen.set(state.dti >= 0.36 || state.creditHealth.utilizationPct > 30);
      this.assetsDetailsOpen.set(false);
      this.billsDetailsOpen.set(this.urgentBillsCount(state) > 0);
      this._lastInitializedSessionId.set(id);
    });
  }

  readonly state = computed((): ClientFinancesState | null => {
    const id = this._sessionId();
    if (!id) return null;
    return this._stateService.storeSnapshot()[id] ?? null;
  });

  readonly prioritizedInsights = computed(() => {
    const state = this.state();
    if (!state) return [];

    const weight: Record<InsightSeverity, number> = {
      danger: 0,
      warn: 1,
      info: 2,
    };

    return [...state.insights]
      .sort((a, b) => {
        const bySeverity = weight[a.severity] - weight[b.severity];
        if (bySeverity !== 0) return bySeverity;
        return a.title.localeCompare(b.title);
      })
      .map((insight, index) => ({ insight, rank: index + 1 }));
  });

  // ---------------------------------------------------------------------------
  // Formatting helpers
  // ---------------------------------------------------------------------------

  formatUsd(value: number): string {
    return USD.format(value);
  }

  formatPct(value: number): string {
    return PCT.format(value);
  }

  formatDti(dti: number): string {
    return `${Math.round(dti * 100)}%`;
  }

  // ---------------------------------------------------------------------------
  // Credit health helpers
  // ---------------------------------------------------------------------------

  bandSeverity(band: CreditScoreBand): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (band) {
      case 'Excellent': return 'success';
      case 'Very Good': return 'success';
      case 'Good':      return 'info';
      case 'Fair':      return 'warn';
      case 'Poor':      return 'danger';
      default:          return 'secondary';
    }
  }

  /** Normalize 12 trend points to SVG polyline points in a 200×40 viewBox. */
  sparklinePoints(state: ClientFinancesState): string {
    const scores = state.creditHealth.trend.map(t => t.score);
    const min = Math.min(...scores);
    const max = Math.max(...scores);
    const range = max - min || 1;
    const w = 200;
    const h = 40;
    const pad = 2;
    return scores
      .map((s, i) => {
        const x = pad + (i / (scores.length - 1)) * (w - pad * 2);
        const y = pad + (1 - (s - min) / range) * (h - pad * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  // ---------------------------------------------------------------------------
  // Debt helpers
  // ---------------------------------------------------------------------------

  debtTypeLabel(type: DebtType): string {
    const labels: Record<DebtType, string> = {
      credit_card: 'Credit Card',
      medical:     'Medical',
      education:   'Education',
      personal:    'Personal Loan',
    };
    return labels[type];
  }

  debtRiskSeverity(risk: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (risk === 'high')   return 'danger';
    if (risk === 'medium') return 'warn';
    return 'success';
  }

  /** Returns percentage-width segments for the debt breakdown bar. */
  debtSegments(debts: DebtItem[]): { type: DebtType; pct: number }[] {
    const total = debts.reduce((s, d) => s + d.balance, 0);
    if (total === 0) return [];
    const byType = new Map<DebtType, number>();
    for (const d of debts) {
      byType.set(d.type, (byType.get(d.type) ?? 0) + d.balance);
    }
    return Array.from(byType.entries()).map(([type, bal]) => ({
      type,
      pct: (bal / total) * 100,
    }));
  }

  totalDebt(debts: DebtItem[]): number {
    return debts.reduce((s, d) => s + d.balance, 0);
  }

  // ---------------------------------------------------------------------------
  // DTI helpers
  // ---------------------------------------------------------------------------

  dtiRiskLabel(dti: number): string {
    if (dti < 0.28) return 'Low';
    if (dti < 0.36) return 'Moderate';
    if (dti < 0.50) return 'High';
    return 'Very High';
  }

  dtiRiskSeverity(dti: number): 'success' | 'info' | 'warn' | 'danger' {
    if (dti < 0.28) return 'success';
    if (dti < 0.36) return 'info';
    if (dti < 0.50) return 'warn';
    return 'danger';
  }

  // ---------------------------------------------------------------------------
  // Emergency fund helpers
  // ---------------------------------------------------------------------------

  emergencyMonths(state: ClientFinancesState): number {
    if (state.monthlyExpenses === 0) return 0;
    return state.emergencySavings / state.monthlyExpenses;
  }

  emergencyPct(state: ClientFinancesState): number {
    return Math.min(100, (this.emergencyMonths(state) / 6) * 100);
  }

  // ---------------------------------------------------------------------------
  // Bill helpers
  // ---------------------------------------------------------------------------

  billRiskSeverity(risk: BillRisk): 'danger' | 'warn' | 'info' | 'secondary' {
    switch (risk) {
      case 'overdue':   return 'danger';
      case 'due_soon':  return 'warn';
      case 'upcoming':  return 'info';
      default:          return 'secondary';
    }
  }

  billRiskLabel(risk: BillRisk): string {
    switch (risk) {
      case 'overdue':   return 'Overdue';
      case 'due_soon':  return 'Due Soon';
      case 'upcoming':  return 'Upcoming';
      default:          return 'Scheduled';
    }
  }

  // ---------------------------------------------------------------------------
  // Insight helpers
  // ---------------------------------------------------------------------------

  insightSeverityClass(severity: InsightSeverity): string {
    switch (severity) {
      case 'danger': return 'bai-insight--danger';
      case 'warn':   return 'bai-insight--warn';
      default:       return 'bai-insight--info';
    }
  }

  insightTagSeverity(severity: InsightSeverity): 'danger' | 'warn' | 'info' {
    return severity === 'danger' ? 'danger' : severity === 'warn' ? 'warn' : 'info';
  }

  insightActionLabel(severity: InsightSeverity): string {
    if (severity === 'danger') return 'Create Action';
    if (severity === 'warn') return 'Message Client';
    return 'Add to Notes';
  }

  insightPriorityLabel(severity: InsightSeverity): 'Now' | 'Next' | 'Later' {
    if (severity === 'danger') return 'Now';
    if (severity === 'warn') return 'Next';
    return 'Later';
  }

  highRiskDebtCount(state: ClientFinancesState): number {
    return state.debts.filter((debt) => debt.risk === 'high').length;
  }

  urgentBillsCount(state: ClientFinancesState): number {
    return state.bills.filter((bill) => bill.risk === 'overdue' || bill.risk === 'due_soon').length;
  }

  toggleSection(section: 'credit' | 'debt' | 'affordability' | 'assets' | 'bills'): void {
    if (section === 'credit') this.creditDetailsOpen.update((v) => !v);
    if (section === 'debt') this.debtDetailsOpen.update((v) => !v);
    if (section === 'affordability') this.affordabilityDetailsOpen.update((v) => !v);
    if (section === 'assets') this.assetsDetailsOpen.update((v) => !v);
    if (section === 'bills') this.billsDetailsOpen.update((v) => !v);
  }

  // ---------------------------------------------------------------------------
  // Track-by helpers
  // ---------------------------------------------------------------------------

  trackDebt(_: number, d: DebtItem): string { return d.id; }
  trackBill(_: number, b: Bill): string { return b.id; }
  trackInsight(_: number, i: FinancialInsight): string { return i.id; }
  trackIncome(_: number, s: IncomeSource): string { return s.label; }
  trackAsset(_: number, a: AssetCategory): string { return a.category; }

  /** Reducer for summing asset values in the template. */
  readonly toSum = (acc: number, a: AssetCategory) => acc + a.value;

  addInsightToCopilot(insight: FinancialInsight): void {
    this._copilotComposer.addContext({
      sourceType: 'finances-insight',
      sourceId: insight.id,
      label: `Finance Insight: ${this._truncate(insight.title, 20)}`,
      fullText:
        `[Finances | Insight | ${insight.id}] ` +
        `Title: ${insight.title}; ` +
        `Severity: ${insight.severity}; ` +
        `Summary: ${insight.body}; ` +
        `Suggested Action: ${insight.suggestedAction}`,
    });
  }

  addDebtToCopilot(debt: DebtItem): void {
    this._copilotComposer.addContext({
      sourceType: 'finances-debt',
      sourceId: debt.id,
      label: `Debt: ${this._truncate(debt.lender, 20)}`,
      fullText:
        `[Finances | Debt | ${debt.id}] ` +
        `Lender: ${debt.lender}; ` +
        `Type: ${this.debtTypeLabel(debt.type)}; ` +
        `Balance: ${this.formatUsd(debt.balance)}; ` +
        `APR: ${debt.apr !== null ? `${debt.apr.toFixed(2)}%` : '—'}; ` +
        `Min Payment: ${debt.minPayment !== null ? `${this.formatUsd(debt.minPayment)}/mo` : '—'}; ` +
        `Risk: ${debt.risk}`,
    });
  }

  addBillToCopilot(bill: Bill): void {
    this._copilotComposer.addContext({
      sourceType: 'finances-bill',
      sourceId: bill.id,
      label: `Bill: ${this._truncate(bill.name, 20)}`,
      fullText:
        `[Finances | Bill | ${bill.id}] ` +
        `Bill: ${bill.name}; ` +
        `Category: ${bill.category}; ` +
        `Amount: ${this.formatUsd(bill.amount)}; ` +
        `Due Date: ${bill.dueDate}; ` +
        `Status: ${this.billRiskLabel(bill.risk)}`,
    });
  }

  private _truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }
}
